/**
 * InviteByEmailModal Component
 * Modal for inviting members via email with quota allocation
 */
import { useState } from 'react';
import { CreateInvitationRequest, InvitationCreated } from '../../api/organizations';
import { XMarkIcon, ClipboardDocumentIcon, CheckIcon } from './icons';

interface InviteByEmailModalProps {
  isOpen: boolean;
  onClose: () => void;
  organizationId: string;
  availableQuotas: {
    slots: number;
    conversations: number;
    aiRequests: number;
  };
  onInvite: (data: CreateInvitationRequest) => Promise<InvitationCreated>;
  onSuccess: () => void;
}

export default function InviteByEmailModal({
  isOpen,
  onClose,
  availableQuotas,
  onInvite,
  onSuccess,
}: InviteByEmailModalProps) {
  const [email, setEmail] = useState('');
  const [clipboardSlots, setClipboardSlots] = useState(5);
  const [conversations, setConversations] = useState(3);
  const [aiRequests, setAiRequests] = useState(100);
  const [expiryDays, setExpiryDays] = useState<7 | 14 | 30>(14);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [invitation, setInvitation] = useState<InvitationCreated | null>(null);
  const [linkCopied, setLinkCopied] = useState(false);

  if (!isOpen) return null;

  // Reset form
  const handleClose = () => {
    setEmail('');
    setClipboardSlots(5);
    setConversations(3);
    setAiRequests(100);
    setExpiryDays(14);
    setError(null);
    setInvitation(null);
    setLinkCopied(false);
    onClose();
  };

  // Validate email
  const isValidEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // Handle invitation submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    // Validation
    if (!email.trim()) {
      setError('Email address is required');
      return;
    }

    if (!isValidEmail(email)) {
      setError('Please enter a valid email address');
      return;
    }

    if (clipboardSlots > availableQuotas.slots) {
      setError(`Cannot allocate more than ${availableQuotas.slots} clipboard slots`);
      return;
    }

    if (conversations > availableQuotas.conversations) {
      setError(`Cannot allocate more than ${availableQuotas.conversations} conversations`);
      return;
    }

    if (aiRequests > availableQuotas.aiRequests) {
      setError(`Cannot allocate more than ${availableQuotas.aiRequests} AI requests`);
      return;
    }

    if (clipboardSlots < 1 || conversations < 1 || aiRequests < 1) {
      setError('All quotas must be at least 1');
      return;
    }

    setLoading(true);

    try {
      const result = await onInvite({
        email: email.trim(),
        expiry_days: expiryDays,
      });

      setInvitation(result);
      onSuccess();
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to send invitation');
    } finally {
      setLoading(false);
    }
  };

  // Copy link to clipboard
  const handleCopyLink = async () => {
    if (!invitation) return;

    try {
      await navigator.clipboard.writeText(invitation.invitation_url);
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy link:', err);
    }
  };

  // Calculate remaining quotas
  const remaining = {
    slots: availableQuotas.slots - clipboardSlots,
    conversations: availableQuotas.conversations - conversations,
    aiRequests: availableQuotas.aiRequests - aiRequests,
  };

  // Success state
  if (invitation) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm">
        <div className="bg-dark-card rounded-2xl shadow-xl w-full max-w-md mx-4 max-h-[90vh] overflow-y-auto">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-dark-border">
            <h2 className="text-xl font-semibold text-white">Invitation Sent Successfully!</h2>
            <button
              onClick={handleClose}
              className="text-dark-text-muted hover:text-white transition-colors"
            >
              <XMarkIcon className="w-6 h-6" />
            </button>
          </div>

          {/* Content */}
          <div className="p-6 space-y-6">
            {/* Success icon */}
            <div className="flex justify-center">
              <div className="w-16 h-16 rounded-full bg-green-500/20 flex items-center justify-center">
                <CheckIcon className="w-8 h-8 text-green-500" />
              </div>
            </div>

            <p className="text-center text-dark-text">
              Invitation email sent to <strong>{email}</strong>
            </p>

            {/* Invitation link */}
            <div>
              <label className="block text-sm font-medium text-dark-text mb-2">
                Invitation Link:
              </label>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={invitation.invitation_url}
                  readOnly
                  className="flex-1 bg-dark-bg/50 border border-dark-border/50 rounded-lg px-4 py-2.5
                           text-sm text-dark-text
                           focus:outline-none focus:ring-2 focus:ring-accent-blue/50"
                />
                <button
                  onClick={handleCopyLink}
                  className="px-4 py-2.5 bg-dark-bg/50 border border-dark-border rounded-lg
                           hover:bg-dark-bg hover:border-accent-blue
                           transition-colors"
                  title="Copy link"
                >
                  {linkCopied ? (
                    <CheckIcon className="w-5 h-5 text-green-500" />
                  ) : (
                    <ClipboardDocumentIcon className="w-5 h-5 text-dark-text" />
                  )}
                </button>
              </div>
            </div>

            {/* Info box */}
            <div className="bg-accent-blue/10 border border-accent-blue/30 rounded-lg p-4">
              <p className="text-sm text-dark-text-muted">
                Share this link directly if the email doesn't arrive. Link expires in {expiryDays} days.
              </p>
            </div>
          </div>

          {/* Footer */}
          <div className="flex gap-3 p-6 border-t border-dark-border">
            <button
              onClick={() => {
                setInvitation(null);
                setEmail('');
                setLinkCopied(false);
              }}
              className="flex-1 px-4 py-3 text-white bg-dark-card border border-dark-border
                       rounded-lg hover:bg-dark-bg
                       transition-colors"
            >
              Invite Another
            </button>
            <button
              onClick={handleClose}
              className="flex-1 px-4 py-3 text-white bg-gradient-to-r from-accent-blue to-accent-orange
                       rounded-lg hover:shadow-lg hover:shadow-accent-blue/50
                       transition-all"
            >
              Done
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Form state
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm">
      <div className="bg-dark-card rounded-2xl shadow-xl w-full max-w-md mx-4 max-h-[90vh] overflow-y-auto">
        <form onSubmit={handleSubmit}>
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-dark-border">
            <h2 className="text-xl font-semibold text-white">Invite Member by Email</h2>
            <button
              type="button"
              onClick={handleClose}
              className="text-dark-text-muted hover:text-white transition-colors"
            >
              <XMarkIcon className="w-6 h-6" />
            </button>
          </div>

          {/* Content */}
          <div className="p-6 space-y-5">
            {/* Error message */}
            {error && (
              <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
                <p className="text-sm text-red-300">{error}</p>
              </div>
            )}

            {/* Email field */}
            <div>
              <label className="block text-sm font-medium text-dark-text mb-2">
                Email Address <span className="text-red-400">*</span>
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="member@example.com"
                className="w-full bg-dark-bg/50 border border-dark-border/50 rounded-xl px-4 py-3
                         text-dark-text placeholder-gray-500
                         focus:outline-none focus:ring-2 focus:ring-accent-blue/50 focus:border-accent-blue
                         transition-all"
                required
              />
            </div>

            {/* Quota allocations */}
            <div>
              <div className="text-sm font-semibold text-dark-text mb-3">Quota Allocations</div>

              <div className="bg-dark-bg/30 border border-dark-border/50 rounded-lg p-4 space-y-4">
                {/* Clipboard slots */}
                <div>
                  <label className="block text-sm text-dark-text mb-2">
                    Clipboard Slots
                  </label>
                  <div className="flex items-center gap-3">
                    <input
                      type="number"
                      min="1"
                      max={availableQuotas.slots}
                      value={clipboardSlots}
                      onChange={(e) => setClipboardSlots(parseInt(e.target.value) || 1)}
                      className="w-24 bg-dark-bg/50 border border-dark-border/50 rounded-lg px-3 py-2
                               text-dark-text
                               focus:outline-none focus:ring-2 focus:ring-accent-blue/50"
                    />
                    <span className="text-sm text-dark-text-muted">
                      / {availableQuotas.slots} available
                    </span>
                  </div>
                </div>

                {/* Conversations */}
                <div>
                  <label className="block text-sm text-dark-text mb-2">
                    Conversations
                  </label>
                  <div className="flex items-center gap-3">
                    <input
                      type="number"
                      min="1"
                      max={availableQuotas.conversations}
                      value={conversations}
                      onChange={(e) => setConversations(parseInt(e.target.value) || 1)}
                      className="w-24 bg-dark-bg/50 border border-dark-border/50 rounded-lg px-3 py-2
                               text-dark-text
                               focus:outline-none focus:ring-2 focus:ring-accent-blue/50"
                    />
                    <span className="text-sm text-dark-text-muted">
                      / {availableQuotas.conversations} available
                    </span>
                  </div>
                </div>

                {/* AI requests */}
                <div>
                  <label className="block text-sm text-dark-text mb-2">
                    AI Requests (monthly)
                  </label>
                  <div className="flex items-center gap-3">
                    <input
                      type="number"
                      min="1"
                      max={availableQuotas.aiRequests}
                      value={aiRequests}
                      onChange={(e) => setAiRequests(parseInt(e.target.value) || 1)}
                      className="w-24 bg-dark-bg/50 border border-dark-border/50 rounded-lg px-3 py-2
                               text-dark-text
                               focus:outline-none focus:ring-2 focus:ring-accent-blue/50"
                    />
                    <span className="text-sm text-dark-text-muted">
                      / {availableQuotas.aiRequests} available
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Expiration */}
            <div>
              <label className="block text-sm font-medium text-dark-text mb-2">
                Expiration
              </label>
              <div className="flex gap-3">
                {[7, 14, 30].map((days) => (
                  <label
                    key={days}
                    className="flex items-center gap-2 cursor-pointer"
                  >
                    <input
                      type="radio"
                      name="expiry"
                      value={days}
                      checked={expiryDays === days}
                      onChange={() => setExpiryDays(days as 7 | 14 | 30)}
                      className="w-4 h-4 text-accent-blue bg-dark-bg border-dark-border
                               focus:ring-2 focus:ring-accent-blue"
                    />
                    <span className="text-sm text-dark-text">{days} days</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Summary */}
            <div className="bg-accent-blue/10 border border-accent-blue/30 rounded-lg p-4">
              <div className="text-sm font-semibold text-accent-blue mb-2">Summary:</div>
              <div className="text-xs text-dark-text-muted space-y-1">
                <div>Available: {availableQuotas.slots} slots, {availableQuotas.conversations} conversations, {availableQuotas.aiRequests} AI</div>
                <div>Allocating: {clipboardSlots} slots, {conversations} conversations, {aiRequests} AI</div>
                <div className={remaining.slots < 0 || remaining.conversations < 0 || remaining.aiRequests < 0 ? 'text-red-400' : ''}>
                  Remaining: {remaining.slots} slots, {remaining.conversations} conversations, {remaining.aiRequests} AI
                </div>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="flex gap-3 p-6 border-t border-dark-border">
            <button
              type="button"
              onClick={handleClose}
              className="flex-1 px-4 py-3 text-white bg-dark-card border border-dark-border
                       rounded-lg hover:bg-dark-bg
                       transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 px-4 py-3 text-white bg-gradient-to-r from-accent-blue to-accent-orange
                       rounded-lg hover:shadow-lg hover:shadow-accent-blue/50
                       disabled:opacity-50 disabled:cursor-not-allowed
                       transition-all"
            >
              {loading ? 'Sending...' : 'Send Invitation'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
